// Interactive lookup by staff (FR-45). Staff state a purpose code; the result
// shows only the consented fields, and the person sees the lookup in their
// access history. "Not found" and "no consent" are one answer (P2-1), so the
// page shows one neutral message and never guesses why. With live approval
// (Phase 9) a lookup first waits for the person's approval: the page says so
// and offers to look up again, which collects the fields once approved.
import { Alert, Button, Code, Group, Paper, Stack, Table, Text, TextInput, Title } from "@mantine/core";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "../../api/client";
import { errorMessage } from "../../shared/session/errors";
import { FieldBadges, useFormat } from "../../shared/ui/format";
import { FormError } from "../../shared/ui/Page";

type Result = Schemas["ResolveResult"];
type Pending = Schemas["AccessPending"];

export const PURPOSE_PATTERN = /^[a-z][a-z0-9_]{1,31}$/;

export function LookupTab({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const f = useFormat();
  const [code, setCode] = useState("");
  const [purpose, setPurpose] = useState("");
  const [purposeError, setPurposeError] = useState<string | null>(null);
  const [result, setResult] = useState<Result | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    setResult(null);
    setPending(null);
    setNotFound(false);
    setError(null);
    const p = purpose.trim();
    if (!PURPOSE_PATTERN.test(p)) return setPurposeError(t("lookup.purpose_invalid"));
    setPurposeError(null);
    setBusy(true);
    const { data, error, response } = await api.POST("/companies/{company_id}/lookup", {
      params: { path: { company_id: companyId } },
      body: { code: code.trim().toUpperCase(), purpose: p },
    });
    setBusy(false);
    if (data && "access_request_id" in data) return setPending(data);
    if (data) return setResult(data);
    if (response.status === 404) return setNotFound(true);
    setError(errorMessage(error, t("error.generic")));
  };

  return (
    <Stack gap="md">
      <Text size="sm">{t("lookup.intro")}</Text>
      <Paper withBorder p="md" radius="md">
        <form onSubmit={submit} data-testid="lookup-form">
          <Stack gap="sm">
            <Group align="flex-start" gap="sm" grow preventGrowOverflow={false}>
              <TextInput
                label={t("lookup.code")}
                description={t("lookup.code_hint")}
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                autoCapitalize="characters"
                autoComplete="off"
                spellCheck={false}
                required
                miw={180}
              />
              <TextInput
                label={t("lookup.purpose")}
                description={t("lookup.purpose_hint")}
                value={purpose}
                onChange={(e) => setPurpose(e.currentTarget.value)}
                autoCapitalize="none"
                autoComplete="off"
                spellCheck={false}
                error={purposeError}
                required
                miw={180}
              />
            </Group>
            <FormError message={error} />
            <Button type="submit" loading={busy} w="fit-content">
              {t("lookup.submit")}
            </Button>
          </Stack>
        </form>
      </Paper>
      {pending && (
        <Alert color="brand" role="status" title={t("lookup.pending_title")} data-testid="lookup-pending">
          <Stack gap="xs">
            <Text size="sm">
              {pending.status === "approved"
                ? t("lookup.pending_approved")
                : t("lookup.pending_body", { time: f.dateTime(pending.expires_at) })}
            </Text>
            <Button size="xs" variant="light" w="fit-content" loading={busy} onClick={() => void submit()}>
              {t("lookup.check_again")}
            </Button>
          </Stack>
        </Alert>
      )}
      {notFound && (
        <Alert color="gray" role="status" data-testid="lookup-not-found">
          {t("lookup.not_found")}
        </Alert>
      )}
      {result && (
        <Paper withBorder p="md" radius="md" data-testid="lookup-result">
          <Stack gap="sm">
            <Title order={2} size="h4">
              {t("lookup.result_title")}
            </Title>
            <FieldBadges fields={result.fields} />
            <Table aria-label={t("lookup.result_title")}>
              <Table.Tbody>
                {result.fields.map((field) => (
                  <Table.Tr key={field}>
                    <Table.Th scope="row" w="40%">
                      {f.field(field)}
                    </Table.Th>
                    <Table.Td data-testid={`lookup-${field}`} style={{ wordBreak: "break-all" }}>
                      {result[field] ?? "—"}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <Text size="xs" c="dimmed">
              {t("lookup.recorded")} <Code>{result.access_id}</Code>
            </Text>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
